import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiDataTable } from 'components/common'
import { Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { deleteInstallationOption, getInstallationOptions } from 'api'
import { Link as NavLink, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'

const BreadcrumbNav = [
  {
    label: 'Installation Options',
    isCurrentPage: true
  }
]

const INSTALLATION_OPTIONS_COLUMNS = [
  {
    label: 'Description',
    orderBy: 'description',
    render: ({ description }) => description
  },
  {
    label: 'cost',
    orderBy: 'cost',
    render: ({ cost }) => cost
  },
  {
    label: 'Actions',
    render: ({ onRemove, ...rest }) => (
      <Flex columnGap={2}>
        <Button as={NavLink} to={`/system/installation-options/edit/${rest.id}`} size="xs" colorScheme="blue">Edit</Button>
        <Button size="xs" colorScheme="red" onClick={() => onRemove(rest)}>Remove</Button>
      </Flex>
    )
  }
]

export const InstallationOptionList = () => {
  const navigate = useNavigate()
  const disclosure = useDisclosure()
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'description',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getInstallationOptions(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onRemove = (option) => {
    setSelectedOption(option)
    disclosure.onOpen()
  }

  const handleRemove = async (optionId) => {
    setIsRemoving(true)
    try {
      await deleteInstallationOption(optionId)
      setIsRemoving(false)
      disclosure.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, onRemove }))
  }, [rows])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Installation Options</Heading>
          <Button
            size="md"
            colorScheme="blue"
            onClick={() => navigate('/system/installation-options/add')}
            leftIcon={<MdAdd size="24px" color="white" />}
          >
            Add Option
          </Button>
        </Flex>
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="id"
          columns={INSTALLATION_OPTIONS_COLUMNS}
          isLoading={isLoading}
        />
      </Box>
      <ModalRemoveInstallationOption
        option={selectedOption}
        isRemoving={isRemoving}
        handleRemove={() => handleRemove(selectedOption?.id)}
        {...disclosure}
      />
    </>
  )
}

const ModalRemoveInstallationOption = ({ option, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Option</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the option - {option?.description}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveInstallationOption.propTypes = {
  option: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
